import React from 'react';
import classnames from 'classnames';
import { initAppElement } from 'common/components/hello-modal';
import { DisplayField, signatureRequestContext, zoomContext, ORIGIN_VIEWPORT } from 'hellospa/components/signature-request';
import { FormattedMessage } from 'react-intl';
import CONSTANTS from 'common/signature-modal/constants';
import signatureModalContext from 'common/signature-modal/context';
import SignatureModal from 'hellospa/components/signature-modal';
import { signerContext } from './context';
import styles from './signature-placeholder.scss';

function useMenu(
  fromScreenCoords,
  isActive,
  fieldData,
  handleOpen,
  insertSignature,
  canInsertEverywhere,
) {
  const [placement, setPlacement] = React.useState('top');
  const menuRef = React.useRef();
  React.useEffect(() => {
    if (menuRef.current && placement === 'top') {
      // fieldData will provide the pageIndex and other position values, but the
      // only thing we care about here is converting the height of the menu to
      // address coordinates.
      const { height } = fromScreenCoords({
        ...fieldData,
        height: menuRef.current.clientHeight,
      }, ORIGIN_VIEWPORT);

      // If there isn't enough room on the page to put the menu on top, switch
      // to the bottom.
      if (height > fieldData.y) {
        setPlacement('bottom');
      }
    }
  }, [fieldData, placement, fromScreenCoords]);

  const handleClear = React.useCallback(
    (e) => {
      e.preventDefault();
      insertSignature(null, fieldData, false);
    },
    [fieldData, insertSignature],
  );
  const handleInsertEverywhere = React.useCallback(
    (e) => {
      e.preventDefault();
      insertSignature(fieldData.signature, fieldData, true);
    },
    [fieldData, insertSignature],
  );

  // No menu if it's readOnly
  if (fieldData.readOnly) {
    return null;
  } else {
    return isActive && fieldData.signature != null && (
      <div ref={menuRef} className={classnames(styles.menuPosition, styles[placement])}>
        <span className={classnames(styles.arrow, styles[placement])} />
        <div className={styles.menu}>
          {canInsertEverywhere && (
            <a className={styles.menuItem} href="#" onClick={handleInsertEverywhere}>
              <FormattedMessage
                id="signer.document.signature.insertEverywhereLabelText"
                description="tooltip link text in signer flow, when clicked inserts the inputted data to all similar fields"
                defaultMessage="Insert everywhere" />
            </a>
          )}
          <a className={styles.menuItem} href="#" onClick={handleOpen}>
            <FormattedMessage
              id="signer.document.signature.editLabelText"
              description="tooltip link text in signer flow, when clicked signer can edit the inputted data"
              defaultMessage="Edit" />
          </a>
          <a className={styles.menuItem} href="#" onClick={handleClear}>
            <FormattedMessage
              id="signer.document.signature.clearLabelText"
              description="tooltip link text in signer flow, when clicked clears the inputted data"
              defaultMessage="Clear" />
          </a>
        </div>
      </div>
    );
  }
}


function SignaturePlaceholder({
  fieldData,
  textScale,
  className,
  ...props
}, ref) {
  initAppElement();
  const signatureModalProps = React.useContext(signatureModalContext);
  const { selectedFieldIds } = React.useContext(signatureRequestContext);
  const { fromScreenCoords } = React.useContext(zoomContext);
  const {
    insertSignature, startEnabled, activeSignatures, emptySignatureCount,
    emptyInitialsCount,
  } = React.useContext(signerContext);

  const [state, setState] = React.useState({
    open: false,
    error: null,
    isCreatingSignatureOrInitials: false,
  });
  const handleOpen = React.useCallback(
    (e) => {
      if (e && typeof e.preventDefault === 'function') {
        e.preventDefault();
      }
      setState((state) => ({ ...state, open: startEnabled }));
    },
    [startEnabled],
  );
  const handleClose = React.useCallback(
    () => setState((state) => ({
      ...state,
      open: false,
      isCreatingSignatureOrInitials: false,
    })),
    [],
  );
  const handleError = React.useCallback(
    (error) => setState((state) => ({ ...state, error })),
    [],
  );
  const handleOpenOrInsert = React.useCallback(() => {
    const isSignatureField = fieldData.type === 'signature';
    // if it's a signature field and there's an active signature available, use that
    if (isSignatureField && activeSignatures.signature != null) {
      insertSignature(activeSignatures.signature, fieldData, false);
      return;
    }

    // if it's an initials field and there's an active initials available, use that
    if (!isSignatureField && activeSignatures.initials != null) {
      insertSignature(activeSignatures.initials, fieldData, false);
      return;
    }

    // otherwise open the modal for the user to do stuff
    handleOpen();
  }, [handleOpen, fieldData, insertSignature, activeSignatures]);
  const handleInsert = React.useCallback(
    (signature, insertEverywhere) => {
      insertSignature(signature, fieldData, insertEverywhere);
      handleClose();
    },
    [fieldData, handleClose, insertSignature],
  );

  const isActive = React.useMemo(
    () => selectedFieldIds.includes(fieldData.id),
    [selectedFieldIds, fieldData.id],
  );


  const handleKeyPress = React.useCallback(
    (e) => {
      // IE: Ctrl +/- are "Unidentified", so it needs to fall back on the keyCode
      let key = e.key !== 'Unidentified' ? e.key : undefined;
      // Safari doesn't support .key, so we have to fall back to .keyCode
      key = key || e.keyCode;
      switch (key) {
        case 'Enter':
        case ' ':
          e.preventDefault();
          return handleOpen();
        default:
      }
    },
    [handleOpen],
  );

  // If we are creating signature/initials
  // We display a message.
  const getCreatingSignatureOrInitialsLoaderContent = () => {
    if (state.isCreatingSignatureOrInitials) {
      return <FormattedMessage
        id="signModal.loader.text"
        description="loading text in editor stating that signature/intials are being saved"
        defaultMessage="Saving your { type, select, S {signature} I {initials} }"
        values={{ type: (fieldData.type === 'signature' ? 'S' : 'I') }} />;
    }
  };

  // I created signatureContext so that the editorContext doesn't have to
  // contain a bunch of things that are only related to <SignatureModal. I
  // placed everything that fetches or manipulates data in the context so some
  // higher level component deals with all of that, and now <SignatureField
  // only has to care about rendering the modal, and attaching whatever the
  // user selects to this field.
  //
  // handleSignatureData makes this difficult, because this function needs to
  // talk to the network (it belongs in the context), but it also inserts the
  // signature just like handleInsert (which belongs here in <SignatureField).
  //
  // In order to work around this, my current approach is to have the
  // `onSignatureData` from context just take `sigData` and return a new `sig`
  // to use. This function's job is to call that promise, then pass the
  // created signature to `handleInsert`.
  const handleSignatureData = async (sigData, insertEverywhere) => {

    // Setting isCreatingSignatureOrInitials to trigger message
    setState((state) => ({
      ...state,
      isCreatingSignatureOrInitials: true,
    }));

    const sig = await signatureModalProps.onSignatureData(sigData);
    const newSig = {
      createType: sigData.create_type_code,
      guid: sig.signature_guid,
      type: sigData.type_code,
    };

    handleInsert(newSig, insertEverywhere);
  };

  const isSignatureType = fieldData.type === 'signature';
  // We need to make sure we reset the savedSignature, because
  // when switching between signatures and initials
  // it is using the same prop: selectedSavedSignature
  let selectedSavedSignature = signatureModalProps.selectedSavedSignature;
  if (selectedSavedSignature !== undefined) {
    if (isSignatureType
        && signatureModalProps.selectedSavedSignature.type === 'I'
        && signatureModalProps.savedSignatures !== undefined
        && signatureModalProps.savedSignatures.length > 0) {
      selectedSavedSignature = signatureModalProps.savedSignatures[0];
    } else if (!isSignatureType
        && signatureModalProps.selectedSavedSignature.type !== 'I'
        && signatureModalProps.savedInitials !== undefined
        && signatureModalProps.savedInitials.length > 0) {
      selectedSavedSignature = signatureModalProps.savedInitials[0];
    }
  }

  const splitProps = (isSignatureType
    ? {
      type: CONSTANTS.TYPE_SIGNATURE,
      initialTypeInValue: signatureModalProps.signatureInitialTypeInValue,
      savedSignatures: signatureModalProps.savedSignatures,
      emailGuid: signatureModalProps.signatureEmailGuid,
    }
    : {
      type: CONSTANTS.TYPE_INITIALS,
      initialTypeInValue: signatureModalProps.initialsInitialTypeInValue,
      savedSignatures: signatureModalProps.savedInitials,
      emailGuid: signatureModalProps.initialsEmailGuid,
    }
  );

  let displayField;
  // if there is no signature and it's not readOnly,
  // allow the signer to interact with the field
  if (fieldData.signature == null && !fieldData.readOnly) {
    displayField = (
      <div
        ref={ref}
        tabIndex={0}
        onKeyPress={handleKeyPress}
        onClick={handleOpenOrInsert}
        style={{ fontSize: `${11 * textScale}px` }}
        className={styles.signaturePlaceholder}
        data-qa-ref={`${fieldData.type}-input`}
      >
        <FormattedMessage
          id="signer.field.label.desktopText"
          description="input text in singer flow, asking user to input signature/initial"
          defaultMessage="{ type, select, signature {Click to sign} initials {Click to initial} text {} date {} }"
          values={{ type: fieldData.type }} />
      </div>
    );
  } else {
    displayField = (
      <DisplayField
        {...props}
        ref={ref}
        tabIndex={0}
        data-qa-ref={`${fieldData.type}-input`}
        className={styles.displayField}
        onKeyPress={handleKeyPress} />
    );
  }

  // if insert everywhere is allowed by sender, check number of signatures on the page
  const canInsertEverywhere = (
    signatureModalProps.canInsertEverywhere
    && fieldData.signature == null
    && (fieldData.type === 'signature' ? emptySignatureCount : emptyInitialsCount) > 1
  );
  const menu = useMenu(
    fromScreenCoords,
    isActive,
    fieldData,
    handleOpen,
    insertSignature,
    canInsertEverywhere,
  );

  const loading = state.isCreatingSignatureOrInitials;
  const loaderContent = getCreatingSignatureOrInitialsLoaderContent();

  const commonSigModalProps = {
    ...signatureModalProps,
    ...splitProps,
    loaderContent,
    loading,
    canInsertEverywhere,
    selectedSavedSignature,
    error: state.error,
    onClose: handleClose,
    onInsert: handleInsert,
    onSignatureData: handleSignatureData,
    onError: handleError,
  };

  return (
    <React.Fragment>
      {displayField}
      {menu}
      <SignatureModal isOpen={state.open} {...commonSigModalProps} />
    </React.Fragment>
  );
}

export default React.forwardRef(SignaturePlaceholder);
